import {
  Badge,
  Button,
  HStack,
  NumberDecrementStepper,
  NumberIncrementStepper,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  Switch,
  Text,
} from '@chakra-ui/react';
import { useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { GiComputing } from 'react-icons/gi';
import { CellProps, Column } from 'react-table';

import { DataTable } from '../../components/Table/DataTable';
import { useUpdateAutomationStatusMutation } from '../../hooks/mutations/useUpdateAutomationStatusMutation';
import useAutomationStatusQuery, { AutomationStatus } from '../../hooks/useAutomationStatusQuery';
import { ContentCard } from '../../layout/ContentCard';

const STATUS_ENABLED = 1;
const STATUS_ERROR = 2;

const AutomationsOverviewCard = () => {
  const { t } = useTranslation();
  const automationsQuery = useAutomationStatusQuery();
  const updateMutation = useUpdateAutomationStatusMutation();

  const automations = (automationsQuery.data?.data ?? []) as AutomationStatus[];
  const [pendingRates, setPendingRates] = useState<Record<number, number>>({});

  // Ref gives cells live access to mutation state without adding it to useMemo deps.
  const mutationRef = useRef(updateMutation);
  mutationRef.current = updateMutation;

  const columns = useMemo<Column<AutomationStatus>[]>(
    () => [
      {
        Header: t('pages:automations.table.columns.name'),
        accessor: 'name' as const,
        Cell: ({ row }: CellProps<AutomationStatus>) => {
          const automation = row.original;
          return (
            <>
              <Text fontWeight="semibold" fontSize="sm">
                {automation.name}
              </Text>
              {automation.description && (
                <Text fontSize="xs" color="gray.500" mt={0.5}>
                  {automation.description}
                </Text>
              )}
              {automation.status === STATUS_ERROR && (
                <Badge colorScheme="red" mt={1} fontSize="xs">
                  {t('pages:automations.status.error')}
                </Badge>
              )}
            </>
          );
        },
      },
      {
        Header: t('pages:automations.table.columns.status'),
        accessor: 'status' as const,
        Cell: ({ row }: CellProps<AutomationStatus>) => {
          const automation = row.original;
          const isEnabled = automation.status === STATUS_ENABLED;
          const isError = automation.status === STATUS_ERROR;
          const isSaving = mutationRef.current.isPending && mutationRef.current.variables?.id === automation.id;

          return (
            <Switch
              isChecked={isEnabled}
              isDisabled={isError || isSaving}
              onChange={() =>
                mutationRef.current.mutate({
                  id: automation.id,
                  status: isEnabled ? 0 : 1,
                })
              }
              colorScheme="green"
            />
          );
        },
      },
      {
        Header: t('pages:automations.table.columns.rate'),
        accessor: 'automation_percentage' as const,
        Cell: ({ row }: CellProps<AutomationStatus>) => {
          const automation = row.original;
          const isSaving = mutationRef.current.isPending && mutationRef.current.variables?.id === automation.id;
          const pendingRate = pendingRates[automation.id];
          const currentRate = pendingRate ?? automation.automation_percentage;
          const hasPendingRate = pendingRate !== undefined;

          return (
            <HStack spacing={2}>
              <NumberInput
                value={currentRate}
                min={0}
                max={100}
                w="90px"
                size="sm"
                onChange={(_, val) =>
                  setPendingRates((prev) => ({
                    ...prev,
                    [automation.id]: isNaN(val) ? 0 : val,
                  }))
                }
              >
                <NumberInputField />
                <NumberInputStepper>
                  <NumberIncrementStepper />
                  <NumberDecrementStepper />
                </NumberInputStepper>
              </NumberInput>
              <Text fontSize="sm" color="gray.500">
                %
              </Text>
              {hasPendingRate && (
                <Button
                  size="xs"
                  variant="primary"
                  isLoading={isSaving}
                  onClick={() => {
                    mutationRef.current.mutate(
                      { id: automation.id, automation_percentage: pendingRate },
                      {
                        onSuccess: () =>
                          setPendingRates((prev) => {
                            const copy = { ...prev };
                            delete copy[automation.id];
                            return copy;
                          }),
                      },
                    );
                  }}
                >
                  {t('common:save')}
                </Button>
              )}
            </HStack>
          );
        },
      },
    ],
    [pendingRates, t],
  );

  return (
    <ContentCard
      icon={<GiComputing size={40} />}
      header={t('pages:automations.table.header')}
      isLoading={automationsQuery.isLoading}
      isFetching={automationsQuery.isFetching}
    >
      <DataTable columns={columns} data={automations} isMoreThenOnePage={false} loading={automationsQuery.isLoading} />
    </ContentCard>
  );
};

export default AutomationsOverviewCard;
